import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  TrendingUp as ProfitIcon,
  AccountBalance as BalanceIcon,
  Receipt as TrialBalanceIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  DateRange as DateIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Types
interface ReportPeriod {
  start_date: string;
  end_date: string;
  period_name: string;
}

interface ProfitLossLine {
  account_code: string;
  account_name: string;
  amount: number;
  amount_formatted: string;
  account_type: string;
  level: number;
}

interface ProfitLossReport {
  period: ReportPeriod;
  revenue: ProfitLossLine[];
  cost_of_sales: ProfitLossLine[];
  gross_profit: number;
  gross_profit_formatted: string;
  operating_expenses: ProfitLossLine[];
  operating_profit: number;
  operating_profit_formatted: string;
  other_income: ProfitLossLine[];
  other_expenses: ProfitLossLine[];
  net_profit: number;
  net_profit_formatted: string;
  vat_summary: {
    total_vat: number;
  };
}

interface BalanceSheetLine {
  account_code: string;
  account_name: string;
  amount: number;
  amount_formatted: string;
  account_type: string;
  level: number;
}

interface BalanceSheetReport {
  period: ReportPeriod;
  current_assets: BalanceSheetLine[];
  fixed_assets: BalanceSheetLine[];
  total_assets: number;
  total_assets_formatted: string;
  current_liabilities: BalanceSheetLine[];
  long_term_liabilities: BalanceSheetLine[];
  total_liabilities: number;
  total_liabilities_formatted: string;
  equity: BalanceSheetLine[];
  total_equity: number;
  total_equity_formatted: string;
  total_liabilities_equity: number;
  total_liabilities_equity_formatted: string;
}

interface TrialBalanceLine {
  account_code: string;
  account_name: string;
  account_type: string;
  debit_balance: number;
  credit_balance: number;
  debit_formatted: string;
  credit_formatted: string;
}

interface TrialBalanceReport {
  period: ReportPeriod;
  accounts: TrialBalanceLine[];
  total_debits: number;
  total_credits: number;
  total_debits_formatted: string;
  total_credits_formatted: string;
  is_balanced: boolean;
}

const FinancialReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<Date>(new Date(new Date().getFullYear(), 0, 1)); // Jan 1st
  const [endDate, setEndDate] = useState<Date>(new Date()); // Today
  const [asOfDate, setAsOfDate] = useState<Date>(new Date()); // Today

  // Report data
  const [profitLossReport, setProfitLossReport] = useState<ProfitLossReport | null>(null);
  const [balanceSheetReport, setBalanceSheetReport] = useState<BalanceSheetReport | null>(null);
  const [trialBalanceReport, setTrialBalanceReport] = useState<TrialBalanceReport | null>(null);

  // Fetch Profit & Loss Report
  const fetchProfitLossReport = async () => {
    setLoading(true);
    try {
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const response = await fetch(`/api/v1/reports/profit-loss?start_date=${startDateStr}&end_date=${endDateStr}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setProfitLossReport(data);
    } catch (error) {
      console.error('Error fetching P&L report:', error);
      toast.error('Failed to load Profit & Loss report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Balance Sheet Report
  const fetchBalanceSheetReport = async () => {
    setLoading(true);
    try {
      const asOfDateStr = asOfDate.toISOString().split('T')[0];

      const response = await fetch(`/api/v1/reports/balance-sheet?as_of_date=${asOfDateStr}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setBalanceSheetReport(data);
    } catch (error) {
      console.error('Error fetching Balance Sheet report:', error);
      toast.error('Failed to load Balance Sheet report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Trial Balance Report
  const fetchTrialBalanceReport = async () => {
    setLoading(true);
    try {
      const asOfDateStr = asOfDate.toISOString().split('T')[0];

      const response = await fetch(`/api/v1/reports/trial-balance?as_of_date=${asOfDateStr}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTrialBalanceReport(data);
    } catch (error) {
      console.error('Error fetching Trial Balance report:', error);
      toast.error('Failed to load Trial Balance report');
    } finally {
      setLoading(false);
    }
  };

  // Load initial reports
  useEffect(() => {
    if (activeTab === 0) {
      fetchProfitLossReport();
    } else if (activeTab === 1) {
      fetchBalanceSheetReport();
    } else if (activeTab === 2) {
      fetchTrialBalanceReport();
    }
  }, [activeTab]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Generate report based on active tab
  const generateReport = () => {
    if (activeTab === 0) {
      fetchProfitLossReport();
    } else if (activeTab === 1) {
      fetchBalanceSheetReport();
    } else if (activeTab === 2) {
      fetchTrialBalanceReport();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            <ReportsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Financial Reports
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              Print
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => toast('Export functionality coming soon!')}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Report Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            <Tab
              icon={<ProfitIcon />}
              label="Profit & Loss"
              iconPosition="start"
            />
            <Tab
              icon={<BalanceIcon />}
              label="Balance Sheet"
              iconPosition="start"
            />
            <Tab
              icon={<TrialBalanceIcon />}
              label="Trial Balance"
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* Date Controls */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            {activeTab === 0 ? (
              // P&L uses date range
              <>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(date) => setStartDate(date || new Date())}
                    slotProps={{
                      textField: { fullWidth: true }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(date) => setEndDate(date || new Date())}
                    slotProps={{
                      textField: { fullWidth: true }
                    }}
                  />
                </Grid>
              </>
            ) : (
              // Balance Sheet and Trial Balance use as-of date
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="As of Date"
                  value={asOfDate}
                  onChange={(date) => setAsOfDate(date || new Date())}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                startIcon={<DateIcon />}
                onClick={generateReport}
                disabled={loading}
                fullWidth
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Report Content */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Profit & Loss Report */}
            {activeTab === 0 && profitLossReport && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom align="center">
                  Profit & Loss Statement
                </Typography>
                <Typography variant="subtitle1" gutterBottom align="center" color="text.secondary">
                  {profitLossReport.period.period_name}
                </Typography>

                <TableContainer>
                  <Table>
                    <TableBody>
                      {/* Revenue Section */}
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="h6" fontWeight="bold">REVENUE</Typography>
                        </TableCell>
                      </TableRow>
                      {profitLossReport.revenue.map((line) => (
                        <TableRow key={line.account_code}>
                          <TableCell sx={{ pl: 4 }}>
                            {line.account_code} - {line.account_name}
                          </TableCell>
                          <TableCell align="right">{line.amount_formatted}</TableCell>
                        </TableRow>
                      ))}
                      {profitLossReport.revenue.length === 0 && (
                        <TableRow>
                          <TableCell sx={{ pl: 4 }} colSpan={2}>
                            <Typography color="text.secondary">No revenue recorded</Typography>
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Cost of Sales Section */}
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="h6" fontWeight="bold">COST OF SALES</Typography>
                        </TableCell>
                      </TableRow>
                      {profitLossReport.cost_of_sales.map((line) => (
                        <TableRow key={line.account_code}>
                          <TableCell sx={{ pl: 4 }}>
                            {line.account_code} - {line.account_name}
                          </TableCell>
                          <TableCell align="right">{line.amount_formatted}</TableCell>
                        </TableRow>
                      ))}
                      {profitLossReport.cost_of_sales.length === 0 && (
                        <TableRow>
                          <TableCell sx={{ pl: 4 }} colSpan={2}>
                            <Typography color="text.secondary">No cost of sales recorded</Typography>
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Gross Profit */}
                      <TableRow sx={{ borderTop: 2, borderColor: 'divider' }}>
                        <TableCell>
                          <Typography variant="h6" fontWeight="bold">GROSS PROFIT</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" fontWeight="bold" color={profitLossReport.gross_profit >= 0 ? 'success.main' : 'error.main'}>
                            {profitLossReport.gross_profit_formatted}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Operating Expenses Section */}
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="h6" fontWeight="bold">OPERATING EXPENSES</Typography>
                        </TableCell>
                      </TableRow>
                      {profitLossReport.operating_expenses.map((line) => (
                        <TableRow key={line.account_code}>
                          <TableCell sx={{ pl: 4 }}>
                            {line.account_code} - {line.account_name}
                          </TableCell>
                          <TableCell align="right">{line.amount_formatted}</TableCell>
                        </TableRow>
                      ))}
                      {profitLossReport.operating_expenses.length === 0 && (
                        <TableRow>
                          <TableCell sx={{ pl: 4 }} colSpan={2}>
                            <Typography color="text.secondary">No operating expenses recorded</Typography>
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Operating Profit */}
                      <TableRow sx={{ borderTop: 2, borderColor: 'divider' }}>
                        <TableCell>
                          <Typography variant="h6" fontWeight="bold">OPERATING PROFIT</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" fontWeight="bold" color={profitLossReport.operating_profit >= 0 ? 'success.main' : 'error.main'}>
                            {profitLossReport.operating_profit_formatted}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Other Income/Expenses */}
                      {(profitLossReport.other_income.length > 0 || profitLossReport.other_expenses.length > 0) && (
                        <>
                          {profitLossReport.other_income.length > 0 && (
                            <>
                              <TableRow>
                                <TableCell colSpan={2}>
                                  <Typography variant="h6" fontWeight="bold">OTHER INCOME</Typography>
                                </TableCell>
                              </TableRow>
                              {profitLossReport.other_income.map((line) => (
                                <TableRow key={line.account_code}>
                                  <TableCell sx={{ pl: 4 }}>
                                    {line.account_code} - {line.account_name}
                                  </TableCell>
                                  <TableCell align="right">{line.amount_formatted}</TableCell>
                                </TableRow>
                              ))}
                            </>
                          )}

                          {profitLossReport.other_expenses.length > 0 && (
                            <>
                              <TableRow>
                                <TableCell colSpan={2}>
                                  <Typography variant="h6" fontWeight="bold">OTHER EXPENSES</Typography>
                                </TableCell>
                              </TableRow>
                              {profitLossReport.other_expenses.map((line) => (
                                <TableRow key={line.account_code}>
                                  <TableCell sx={{ pl: 4 }}>
                                    {line.account_code} - {line.account_name}
                                  </TableCell>
                                  <TableCell align="right">{line.amount_formatted}</TableCell>
                                </TableRow>
                              ))}
                            </>
                          )}
                        </>
                      )}

                      {/* Net Profit */}
                      <TableRow sx={{ borderTop: 3, borderColor: 'primary.main', bgcolor: 'grey.50' }}>
                        <TableCell>
                          <Typography variant="h5" fontWeight="bold">NET PROFIT</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h5" fontWeight="bold" color={profitLossReport.net_profit >= 0 ? 'success.main' : 'error.main'}>
                            {profitLossReport.net_profit_formatted}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* VAT Summary */}
                      <TableRow>
                        <TableCell colSpan={2} sx={{ pt: 3 }}>
                          <Box display="flex" justifyContent="center">
                            <Chip
                              label={`Total VAT: £${profitLossReport.vat_summary.total_vat.toFixed(2)}`}
                              color="info"
                              variant="outlined"
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default FinancialReports;
